import React, { useState } from 'react';
import { 
  User, 
  Edit, 
  Mail, 
  Phone, 
  Calendar,
  Save,
  X,
  CheckCircle,
  UserCheck,
  Shield,
  Search,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { Customer } from '../types/api';

const ClientesSection: React.FC = () => {
  const {
    getCustomerByPhone,
    updateCustomer,
    verifyOtp,
    updateUserClabe,
    getUserClabe
  } = useAdminAPI();

  const [activeTab, setActiveTab] = useState<'datos' | 'contacto' | 'clabe'>('datos');
  const [searchPhone, setSearchPhone] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [customerFound, setCustomerFound] = useState(false);
  const [customerData, setCustomerData] = useState<Customer | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [isEditing, setIsEditing] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Form data for each section
  const [datosForm, setDatosForm] = useState({
    name: '',
    lastName: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const [contactoForm, setContactoForm] = useState({
    email: '',
    phone: ''
  });

  const [clabeForm, setClabeForm] = useState({
    clabe: ''
  });

  const handleRequestPIN = async () => {
    if (!customerData) return;
    // No se envía OTP por email, el usuario ingresa su PIN directamente
    setShowOTPModal(true);
    setError(null);
  };

  const handleVerifyPIN = async () => {
    if (!customerData || !otpCode.trim()) {
      setError('Por favor ingresa el PIN del cliente');
      return;
    }

    try {
      setIsVerifyingOtp(true);
      setError(null);
      
      // El backend debe validar el PIN del cliente directamente
      // El campo "otp" contiene el PIN del usuario que se compara con el PIN almacenado en la BD
      console.log('🔐 Verificando PIN del cliente:', { 
        customerId: customerData.id, 
        pinLength: otpCode.length 
      });

      const response = await verifyOtp(customerData.id, {
        otp: otpCode, // PIN del cliente (el backend debe compararlo con el PIN almacenado del usuario)
        purpose: 'update_contact'
      });
      
      console.log('✅ PIN verificado correctamente');
      
      setOtpVerified(true);
      setVerificationToken(response.token);
      setShowOTPModal(false);
      setOtpCode('');
      setIsEditing(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'PIN incorrecto';
      setError(errorMessage);
      console.error('❌ Error verificando PIN:', err);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSaveDatos = async () => {
    if (!customerData) return;

    try {
      setIsSaving(true);
      setError(null);
      const updated = await updateCustomer(customerData.id, {
        name: datosForm.name,
        lastName: datosForm.lastName,
        dateOfBirth: datosForm.dateOfBirth,
        address: datosForm.address,
        city: datosForm.city,
        state: datosForm.state,
        zipCode: datosForm.zipCode
      });
      
      setCustomerData(updated);
    setIsEditing(false);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar los datos';
      setError(errorMessage);
      console.error('Error updating customer data:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveContacto = async () => {
    if (!customerData) return;

    if (!otpVerified || !verificationToken) {
      setError('Debes verificar el PIN del cliente antes de guardar cambios en contacto');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const updated = await updateCustomer(customerData.id, {
        email: contactoForm.email,
        phone: contactoForm.phone
      });
      
      setCustomerData(updated);
    setIsEditing(false);
    setOtpVerified(false);
      setVerificationToken(null);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar el contacto';
      setError(errorMessage);
      console.error('Error updating contact:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClabe = async () => {
    if (!customerData) return;

    const clabeValue = clabeForm.clabe.trim();
    
    // Validar que la CLABE tenga 18 dígitos si se proporciona
    if (clabeValue && clabeValue.length !== 18) {
      setError('La CLABE debe tener exactamente 18 dígitos');
      return;
    }

    // Si está vacía, no permitir guardar (el backend requiere 18 dígitos)
    if (!clabeValue) {
      setError('Por favor ingrese una CLABE de 18 dígitos');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      
      console.log('💾 Guardando CLABE usando endpoint específico:', { 
        userId: customerData.id, 
        clabe: clabeValue,
        originalClabe: customerData.clabe 
      });
      
      // Usar el nuevo endpoint específico para CLABE
      const updated = await updateUserClabe(customerData.id, clabeValue);
      
      console.log('✅ CLABE actualizada - Respuesta del backend:', { 
        original: customerData.clabe, 
        nueva: updated.clabe,
        fullResponse: updated
      });
      
      // Actualizar el estado local
      setCustomerData(updated);
      setClabeForm({ clabe: updated.clabe || '' });
      setIsEditing(false);
      setError(null);
      
      // Recargar los datos del cliente y la CLABE para asegurar que tenemos la información más actualizada
      try {
        const refreshedCustomer = await getCustomerByPhone(customerData.phone);
        // Obtener la CLABE actualizada usando query parameter con teléfono
        try {
          const normalizedPhone = customerData.phone.replace(/\D/g, '');
          const clabeResponse = await getUserClabe(normalizedPhone, true);
          setClabeForm({ clabe: clabeResponse.clabe || '' });
        } catch (clabeError) {
          console.warn('⚠️ Error al obtener CLABE con teléfono, intentando con userId:', clabeError);
          // Fallback: intentar con userId
          try {
            const clabeResponse = await getUserClabe(refreshedCustomer.id);
            setClabeForm({ clabe: clabeResponse.clabe || '' });
          } catch (fallbackError) {
            console.warn('⚠️ Error al obtener CLABE actualizada del endpoint, usando CLABE de customer:', fallbackError);
            setClabeForm({ clabe: refreshedCustomer.clabe || '' });
          }
        }
        console.log('🔄 Cliente recargado después de guardar CLABE:', {
          clabeEnRespuesta: refreshedCustomer.clabe,
          clabeEnEstado: updated.clabe
        });
        setCustomerData(refreshedCustomer);
      } catch (refreshError) {
        console.error('Error al recargar cliente después de guardar CLABE:', refreshError);
        // No mostrar error al usuario, ya que la actualización fue exitosa
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar la CLABE';
      setError(errorMessage);
      console.error('Error updating CLABE:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearchCustomer = async () => {
    if (!searchPhone.trim()) {
      setError('Por favor ingresa un número de teléfono para buscar');
      return;
    }

    // Normalizar teléfono: remover caracteres no numéricos
    const normalizedPhone = searchPhone.trim().replace(/\D/g, '');
    
    // Validar que tenga 10 dígitos (después de normalizar)
    if (normalizedPhone.length !== 10) {
      setError('El teléfono debe tener exactamente 10 dígitos');
      return;
    }

    setIsSearching(true);
    setError(null);
    setCustomerFound(false);
    
    try {
      const customer = await getCustomerByPhone(normalizedPhone);
      
      // Obtener la CLABE usando el endpoint GET /admin/users/clabe?phone=...
      // Usar query parameter con teléfono en lugar de userId
      let clabeValue = customer.clabe || '';
      try {
        const clabeResponse = await getUserClabe(normalizedPhone, true);
        clabeValue = clabeResponse.clabe || customer.clabe || '';
      } catch (clabeError) {
        console.warn('⚠️ Error al obtener CLABE del endpoint, intentando con userId:', clabeError);
        // Fallback: intentar con userId si falla con teléfono
        try {
          const clabeResponse = await getUserClabe(customer.id);
          clabeValue = clabeResponse.clabe || customer.clabe || '';
        } catch (fallbackError) {
          console.warn('⚠️ Error al obtener CLABE con userId también, usando CLABE de customer:', fallbackError);
        }
      }
      
      setCustomerData(customer);
        setDatosForm({
        name: customer.name,
        lastName: customer.lastName,
        dateOfBirth: customer.dateOfBirth,
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zipCode: customer.zipCode || ''
        });
        setContactoForm({
        email: customer.email,
        phone: customer.phone
        });
        setClabeForm({
        clabe: clabeValue
        });
        setCustomerFound(true);
      setError(null);
    } catch (err) {
        setCustomerFound(false);
      setCustomerData(null);
      const errorMessage = err instanceof Error ? err.message : 'No se encontró ningún cliente con ese número de teléfono';
      setError(errorMessage);
      console.error('Error searching customer:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const tabs = [
    {
      id: 'datos',
      label: 'Datos Parciales',
      icon: User,
      description: 'Editar datos generales del usuario',
      requiresOTP: false
    },
    {
      id: 'contacto',
      label: 'Correo/Telefono',
      icon: Mail,
      description: 'Editar correo electrónico y teléfono',
      requiresOTP: true
    },
    {
      id: 'clabe',
      label: 'CLABE',
      icon: Calendar,
      description: 'Editar CLABE bancaria',
      requiresOTP: false
    }
  ];

  const renderDatosSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Datos Generales del Cliente</h3>
          <p className="text-sm text-secondary-500">Información personal y de dirección</p>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="btn-primary flex items-center space-x-2"
          disabled={isSaving}
        >
          <Edit className="h-4 w-4" />
          <span>{isEditing ? 'Cancelar' : 'Editar'}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Nombre</label>
          <input
            type="text"
            className="input-field"
            value={datosForm.name}
            onChange={(e) => setDatosForm({ ...datosForm, name: e.target.value })}
            disabled={!isEditing || isSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Apellido</label>
          <input
            type="text"
            className="input-field"
            value={datosForm.lastName}
            onChange={(e) => setDatosForm({ ...datosForm, lastName: e.target.value })}
            disabled={!isEditing || isSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Fecha de Nacimiento</label>
          <input
            type="date"
            className="input-field"
            value={datosForm.dateOfBirth}
            onChange={(e) => setDatosForm({ ...datosForm, dateOfBirth: e.target.value })}
            disabled={!isEditing || isSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Código Postal</label>
          <input
            type="text"
            className="input-field"
            value={datosForm.zipCode}
            onChange={(e) => setDatosForm({ ...datosForm, zipCode: e.target.value })}
            disabled={!isEditing || isSaving}
            maxLength={5}
          />
        </div>
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-secondary-700 mb-2">Dirección</label>
          <input
            type="text"
            className="input-field"
            value={datosForm.address}
            onChange={(e) => setDatosForm({ ...datosForm, address: e.target.value })}
            disabled={!isEditing || isSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Ciudad</label>
          <input
            type="text"
            className="input-field"
            value={datosForm.city}
            onChange={(e) => setDatosForm({ ...datosForm, city: e.target.value })}
            disabled={!isEditing || isSaving}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Estado</label>
          <input
            type="text"
            className="input-field"
            value={datosForm.state}
            onChange={(e) => setDatosForm({ ...datosForm, state: e.target.value })}
            disabled={!isEditing || isSaving}
          />
        </div>
      </div>

      {isEditing && (
        <div className="flex space-x-3 pt-4">
          <button
            onClick={() => {
              setIsEditing(false);
              if (customerData) {
                setDatosForm({
                  name: customerData.name,
                  lastName: customerData.lastName,
                  dateOfBirth: customerData.dateOfBirth,
                  address: customerData.address,
                  city: customerData.city,
                  state: customerData.state,
                  zipCode: customerData.zipCode
                });
              }
            }}
            className="btn-secondary flex items-center space-x-2"
            disabled={isSaving}
          >
            <X className="h-4 w-4" />
            <span>Cancelar</span>
          </button>
          <button
            onClick={handleSaveDatos}
            className="btn-primary flex items-center space-x-2"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
            <Save className="h-4 w-4" />
            <span>Guardar Cambios</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderContactoSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Información de Contacto</h3>
          <p className="text-sm text-secondary-500">Correo electrónico y teléfono (requiere OTP)</p>
        </div>
        <div className="flex items-center space-x-2">
          {otpVerified && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center">
              <CheckCircle className="h-3 w-3 mr-1" />
              OTP Verificado
            </span>
          )}
          <button
            onClick={() => {
              if (!otpVerified) {
                handleRequestPIN();
              } else {
                setIsEditing(!isEditing);
              }
            }}
            className="btn-primary flex items-center space-x-2"
            disabled={isSaving}
          >
            <Edit className="h-4 w-4" />
            <span>{isEditing ? 'Cancelar' : 'Editar'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Correo Electrónico</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="email"
              className="input-field pl-10"
              value={contactoForm.email}
              onChange={(e) => setContactoForm({ ...contactoForm, email: e.target.value })}
              disabled={!isEditing || isSaving}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">Teléfono</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="tel"
              className="input-field pl-10"
              value={contactoForm.phone}
              onChange={(e) => setContactoForm({ ...contactoForm, phone: e.target.value })}
              disabled={!isEditing || isSaving}
              maxLength={10}
            />
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="flex space-x-3 pt-4">
          <button
            onClick={() => {
              setIsEditing(false);
              setOtpVerified(false);
              setVerificationToken(null);
              if (customerData) {
                setContactoForm({
                  email: customerData.email,
                  phone: customerData.phone
                });
              }
            }}
            className="btn-secondary flex items-center space-x-2"
            disabled={isSaving}
          >
            <X className="h-4 w-4" />
            <span>Cancelar</span>
          </button>
          <button
            onClick={handleSaveContacto}
            className="btn-primary flex items-center space-x-2"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
            <Save className="h-4 w-4" />
            <span>Guardar Cambios</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderClabeSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">CLABE Bancaria</h3>
          <p className="text-sm text-secondary-500">Editar CLABE bancaria</p>
        </div>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="btn-primary flex items-center space-x-2"
          disabled={isSaving}
        >
          <Edit className="h-4 w-4" />
          <span>{isEditing ? 'Cancelar' : 'Editar'}</span>
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">CLABE</label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <input
            type="text"
            className="input-field pl-10"
            value={clabeForm.clabe}
            onChange={(e) => setClabeForm({ ...clabeForm, clabe: e.target.value })}
              disabled={!isEditing || isSaving}
              placeholder={customerData?.clabe ? customerData.clabe : "No registrada - Ingresa CLABE (18 dígitos)"}
            maxLength={18}
          />
        </div>
        <p className="text-xs text-secondary-500 mt-1">
            {customerData?.clabe 
              ? 'La CLABE debe tener 18 dígitos'
              : 'CLABE no registrada. Ingresa una CLABE de 18 dígitos para registrarla.'
            }
        </p>
      </div>

      {isEditing && (
        <div className="flex space-x-3 pt-4">
          <button
            onClick={() => {
              setIsEditing(false);
              if (customerData) {
                setClabeForm({ clabe: customerData.clabe || '' });
              }
            }}
            className="btn-secondary flex items-center space-x-2"
            disabled={isSaving}
          >
            <X className="h-4 w-4" />
            <span>Cancelar</span>
          </button>
          <button
            onClick={handleSaveClabe}
            className="btn-primary flex items-center space-x-2"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
            <Save className="h-4 w-4" />
            <span>Guardar Cambios</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 bg-[#3bbcc8]/10 rounded-lg flex items-center justify-center">
            <UserCheck className="h-5 w-5 text-[#3bbcc8]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-secondary-900">Gestión de Clientes</h2>
            <p className="text-sm text-secondary-500">Edición parcial de datos de usuarios registrados</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-red-50 border-red-200">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Buscar Cliente</h3>
            <p className="text-sm text-secondary-500">Ingresa el número de teléfono para buscar y editar datos</p>
          </div>
          {customerFound && (
            <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full flex items-center">
              <CheckCircle className="h-3 w-3 mr-1" />
              Cliente Encontrado
            </span>
          )}
        </div>
        
        <div className="flex space-x-3">
          <div className="flex-1 relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="tel"
              className="input-field pl-10"
              placeholder="Ej: 5512345678"
              value={searchPhone}
              onChange={(e) => {
                // Solo permitir números, máximo 10 dígitos
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setSearchPhone(value);
              }}
              maxLength={10}
              disabled={isSearching}
            />
          </div>
          <button
            onClick={handleSearchCustomer}
            disabled={isSearching || !searchPhone.trim()}
            className="btn-primary flex items-center space-x-2 px-6"
          >
            {isSearching ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Buscando...</span>
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                <span>Buscar</span>
              </>
            )}
          </button>
        </div>
        
        {customerFound && customerData && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <UserCheck className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-700">
                Cliente: <strong>{customerData.name} {customerData.lastName}</strong> - {customerData.phone}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="card p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1 bg-secondary-100 p-1 rounded-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 px-3 lg:px-4 py-2 lg:py-3 rounded-md transition-colors duration-200 ${
                  isActive
                    ? 'bg-white text-[#3bbcc8] shadow-sm'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs lg:text-sm font-medium">{tab.label}</span>
                {tab.requiresOTP && (
                  <Shield className="h-3 w-3 text-orange-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="card p-6">
        {!customerFound ? (
          <div className="text-center py-12">
            <User className="h-16 w-16 text-secondary-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No hay cliente seleccionado</h3>
            <p className="text-secondary-600 mb-4">Busca un cliente por número de teléfono para ver y editar su información</p>
          </div>
        ) : (
          <>
            {activeTab === 'datos' && renderDatosSection()}
            {activeTab === 'contacto' && renderContactoSection()}
            {activeTab === 'clabe' && renderClabeSection()}
          </>
        )}
      </div>

      {/* OTP Modal */}
      {showOTPModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md modal-mobile">
            <div className="flex items-center space-x-3 mb-4">
              <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">Verificación PIN</h3>
                <p className="text-sm text-secondary-500">Ingresa el PIN del cliente para continuar</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">PIN del Cliente</label>
                <input
                  type="password"
                  className="input-field text-center text-lg font-mono"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Ingresa PIN"
                  maxLength={6}
                  disabled={isVerifyingOtp}
                  autoFocus
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-700">
                    El cliente debe proporcionar su PIN para autorizar cambios en información de contacto.
                  </span>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowOTPModal(false);
                  setOtpCode('');
                }}
                className="btn-secondary flex-1"
                disabled={isVerifyingOtp}
              >
                Cancelar
              </button>
              <button
                onClick={handleVerifyPIN}
                className="btn-primary flex-1"
                disabled={isVerifyingOtp || !otpCode.trim()}
              >
                {isVerifyingOtp ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                    Verificando...
                  </>
                ) : (
                  'Verificar PIN'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientesSection;
